/**
 * Centralized address utility functions
 * Provides consistent address formatting across the Go Sit Back platform
 */

export interface PropertyAddress {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
}

  export class AddressUtils {
  /**
   * Format a property address object to a readable string
   * @param property - Property with address fields
   * @returns Formatted address string
   */
  static formatPropertyAddress(property: PropertyAddress): string {
    const parts = [
      property.addressLine1,
      property.addressLine2,
      `${property.city}, ${property.state} ${property.postalCode}`
    ].filter(part => part && part.trim() !== '');
    
    return parts.join(', ');
  }

  /**
   * Format a property address with optional line 2
   * @param address - Address string or PropertyAddress object
   * @returns Formatted address string
   */
  static formatAddress(address: string | PropertyAddress | null | undefined): string {
    if (!address) return 'No address';
    
    // If it's already a string, return it
    if (typeof address === 'string') {
      try {
        // Try to parse if it's a JSON string
        const parsed = JSON.parse(address);
        return this.formatPropertyAddress(parsed);
      } catch {
        // If parsing fails, return the string as-is
        return address;
      }
    }
    
    // If it's a PropertyAddress object, format it
    return this.formatPropertyAddress(address);
  }

  /**
   * Parse an address string into components
   * Note: This is a simple parser and may not handle all edge cases
   * @param addressString - Full address string
   * @returns Partial address object with parsed components
   */
  static parseAddress(addressString: string): Partial<PropertyAddress> {
    const parts = addressString.split(',').map(p => p.trim());
    
    if (parts.length < 2) {
      return { addressLine1: addressString };
    }

    const addressLine1 = parts[0];
    const lastPart = parts[parts.length - 1];
    const statePostalCodeMatch = lastPart.match(/([A-Z]{2})\s+(\d{5}(-\d{4})?)/);
    
    if (statePostalCodeMatch && parts.length >= 2) {
      const state = statePostalCodeMatch[1];
      const postalCode = statePostalCodeMatch[2];
      const city = parts[parts.length - 2];
      const addressLine2 = parts.length > 3 ? parts.slice(1, -2).join(', ') : undefined;
      
      return {
        addressLine1,
        addressLine2,
        city,
        state,
        postalCode
      };
    }

    return {
      addressLine1,
      addressLine2: parts.slice(1).join(', ')
    };
  }

  /**
   * Validate if an address has minimum required fields
   * @param address - Address to validate
   * @returns True if address has required fields
   */
  static isValidAddress(address: Partial<PropertyAddress>): address is PropertyAddress {
    return !!(
      address.addressLine1?.trim() &&
      address.city?.trim() &&
      address.state?.trim() &&
      address.postalCode?.trim()
    );
  }

  /**
   * Format address into separate lines for multi-line display
   * @param address - Address string, PropertyAddress object, or null/undefined
   * @returns Array of address line strings, or null if no valid address
   */
  static formatAddressLines(address: string | PropertyAddress | null | undefined): string[] | null {
    if (!address) return null;
    
    let addressObj: PropertyAddress | null = null;
    
    // Convert to PropertyAddress object
    if (typeof address === 'string') {
      try {
        // Try to parse as JSON first
        addressObj = JSON.parse(address) as PropertyAddress;
      } catch {
        // If it's a plain string, try to parse it intelligently
        const parsed = this.parseAddress(address);
        
        // If we successfully parsed it into structured components, format those
        if (this.isValidAddress(parsed)) {
          addressObj = parsed;
        } else {
          // If parsing failed, split by common delimiters
          // Handle formats like "123 Main St, City, ST 12345"
          const parts = address.split(',').map(p => p.trim()).filter(p => p);
          
          if (parts.length >= 2) {
            // Return street on one line, rest on another
            return parts;
          }
          
          // Fallback: return as single line
          return [address];
        }
      }
    } else {
      addressObj = address;
    }
    
    // Build array of lines from structured address
    const lines: string[] = [addressObj.addressLine1];
    if (addressObj.addressLine2) {
      lines.push(addressObj.addressLine2);
    }
    lines.push(`${addressObj.city}, ${addressObj.state} ${addressObj.postalCode}`);
    
    return lines;
  }
}